import { useState } from "react";
import { useLang } from "../lib/useLang";
import { useAuth } from "../state/AuthContext";
import type { Address } from "../lib/types";
import { AddressFields, addressSummary } from "./AddressFields";

const EMPTY: Address = { governorate: "", area: "", block: "", street: "", building: "" };

/** Member address book: list, add, and remove saved delivery addresses. */
export function AddressBook() {
  const { t } = useLang();
  const { user, addAddress, removeAddress } = useAuth();
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");
  const [draft, setDraft] = useState<Address>(EMPTY);

  if (!user) return null;
  const addresses = user.addresses ?? [];

  const valid = draft.area && draft.block && draft.street && draft.building;

  function save() {
    if (!valid) return;
    addAddress(draft, label.trim() || draft.area);
    setDraft(EMPTY);
    setLabel("");
    setAdding(false);
  }

  return (
    <div className="card space-y-3 p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-ink">{t("account.addresses")}</h2>
        {!adding && (
          <button className="btn-outline text-sm" onClick={() => setAdding(true)}>
            ＋ {t("account.addAddress")}
          </button>
        )}
      </div>

      {addresses.length === 0 && !adding && (
        <p className="text-sm text-black/50">{t("account.noAddresses")}</p>
      )}

      {addresses.map((a) => (
        <div
          key={a.id}
          className="flex items-start justify-between gap-3 rounded-xl border border-black/10 p-3"
        >
          <div className="min-w-0">
            <div className="font-semibold text-ink">📍 {a.label}</div>
            <div className="text-xs text-black/50">{addressSummary(a)}</div>
          </div>
          <button
            className="shrink-0 text-sm font-semibold text-black/45 hover:text-brand"
            onClick={() => removeAddress(a.id)}
          >
            {t("common.remove")}
          </button>
        </div>
      ))}

      {adding && (
        <div className="space-y-3 border-t border-black/5 pt-3">
          <input
            className="input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={t("checkout.addressLabel")}
          />
          <AddressFields
            value={draft}
            onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
          />
          <div className="flex gap-2">
            <button className="btn-primary flex-1" onClick={save} disabled={!valid}>
              {t("common.save")}
            </button>
            <button
              className="btn-outline flex-1"
              onClick={() => {
                setAdding(false);
                setDraft(EMPTY);
                setLabel("");
              }}
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
